'use client';

import { useSyncExternalStore } from 'react';

export const MOBILE_LAYOUT_MAX_WIDTH = 767;
export const TABLET_LAYOUT_MAX_WIDTH = 1279;
export const WIDE_LAYOUT_MIN_WIDTH = 1024;

const WIDE_WIDTH_QUERY = `(min-width: ${WIDE_LAYOUT_MIN_WIDTH}px)`;
const COARSE_POINTER_QUERY = '(pointer: coarse)';
const MOBILE_WIDTH_QUERY = `(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`;
const TABLET_WIDTH_QUERY = `(max-width: ${TABLET_LAYOUT_MAX_WIDTH}px)`;

export interface ResponsiveLayoutState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  usesCoarsePointer: boolean;
  /** 配置面板可与内容并排显示的宽度；与触控能力无关。 */
  hasWideViewport: boolean;
  /** 客户端断点已经读取完成；SSR 阶段始终为 false */
  isResolved: boolean;
}

const DEFAULT_LAYOUT_STATE: ResponsiveLayoutState = {
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  usesCoarsePointer: false,
  hasWideViewport: true,
  isResolved: false,
};

let clientLayoutState: ResponsiveLayoutState | null = null;
let mediaQueryCleanup: (() => void) | null = null;
const layoutListeners = new Set<() => void>();

function readMediaQuery(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(query).matches;
}

export function readResponsiveLayoutState(): ResponsiveLayoutState {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return DEFAULT_LAYOUT_STATE;
  }

  const usesCoarsePointer = readMediaQuery(COARSE_POINTER_QUERY);
  const isNarrowViewport = readMediaQuery(MOBILE_WIDTH_QUERY);
  const isTabletViewport = readMediaQuery(TABLET_WIDTH_QUERY);
  // 布局只由可用宽度决定；触控能力独立用于命中区和手势。
  const isMobile = isNarrowViewport;
  const isTablet = !isMobile && isTabletViewport;

  return {
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    usesCoarsePointer,
    hasWideViewport: readMediaQuery(WIDE_WIDTH_QUERY),
    isResolved: true,
  };
}

function bindMediaQueryListener(mediaQuery: MediaQueryList, onChange: () => void): () => void {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }

  mediaQuery.addListener(onChange);
  return () => mediaQuery.removeListener(onChange);
}

function sameLayoutState(left: ResponsiveLayoutState, right: ResponsiveLayoutState): boolean {
  return left.isMobile === right.isMobile
    && left.isTablet === right.isTablet
    && left.isDesktop === right.isDesktop
    && left.hasWideViewport === right.hasWideViewport
    && left.usesCoarsePointer === right.usesCoarsePointer
    && left.isResolved === right.isResolved;
}

function readClientSnapshot(): ResponsiveLayoutState {
  if (!clientLayoutState) {
    clientLayoutState = readResponsiveLayoutState();
  }
  return clientLayoutState;
}

function readServerSnapshot(): ResponsiveLayoutState {
  return DEFAULT_LAYOUT_STATE;
}

function updateClientLayoutState(): void {
  const nextState = readResponsiveLayoutState();
  const previousState = clientLayoutState;
  if (previousState && sameLayoutState(previousState, nextState)) {
    return;
  }

  clientLayoutState = nextState;
  layoutListeners.forEach((listener) => listener());
}

function subscribeToResponsiveLayout(listener: () => void): () => void {
  layoutListeners.add(listener);

  if (!mediaQueryCleanup && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mediaQueries = [
      window.matchMedia(COARSE_POINTER_QUERY),
      window.matchMedia(WIDE_WIDTH_QUERY),
      window.matchMedia(MOBILE_WIDTH_QUERY),
      window.matchMedia(TABLET_WIDTH_QUERY),
    ];
    const cleanupListeners = mediaQueries.map((mediaQuery) => (
      bindMediaQueryListener(mediaQuery, updateClientLayoutState)
    ));
    mediaQueryCleanup = () => {
      cleanupListeners.forEach((cleanup) => cleanup());
      mediaQueryCleanup = null;
    };
  }

  updateClientLayoutState();

  return () => {
    layoutListeners.delete(listener);
    if (layoutListeners.size === 0) {
      mediaQueryCleanup?.();
    }
  };
}

export function useResponsiveLayout(): ResponsiveLayoutState {
  return useSyncExternalStore(
    subscribeToResponsiveLayout,
    readClientSnapshot,
    readServerSnapshot,
  );
}
